import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Pause, Play, Plus, Minus, UserRound, MessageCircle, Mail } from "lucide-react";
import "@/components/sections/AttendeeStories.css";
import { analytics, track } from "@/lib/analytics";
import { eventConfig } from "@/config/eventConfig";
import { ATTENDEE_LABEL, ATTENDEE_STORIES, STORY_EVENTS, STORY_TOPICS } from "@/components/sections/storiesData";

const SPOTLIGHT = ATTENDEE_STORIES.filter((s) => s.spotlight);
const ROTATE_MS = 7000;
const TICK_MS = 100;
const INITIAL_VISIBLE = 6;

const topicLabel = (key) => (STORY_TOPICS.find((t) => t.key === key) || {}).label || key;

const Person = ({ story, compact = false }) => (
  <div className={`cas-person${compact ? " compact" : ""}`}>
    <span className="cas-avatar" aria-hidden="true"><UserRound size={compact ? 14 : 18} /></span>
    <span>
      <strong data-testid={`story-name-${story.id}`}>{ATTENDEE_LABEL}</strong>
      <small>{story.ageBand} · {STORY_EVENTS[story.event]}</small>
    </span>
  </div>
);

/* Featured quote that rotates every 7s (pauses on hover / via button). */
const Spotlight = ({ reduceMotion }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const hover = useRef(false);

  useEffect(() => {
    if (reduceMotion || paused) return undefined;
    const id = window.setInterval(() => {
      if (hover.current) return;
      setElapsed((e) => {
        if (e + TICK_MS >= ROTATE_MS) {
          setIndex((i) => (i + 1) % SPOTLIGHT.length);
          return 0;
        }
        return e + TICK_MS;
      });
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, [paused, reduceMotion]);

  const go = (i) => {
    const next = (i + SPOTLIGHT.length) % SPOTLIGHT.length;
    setIndex(next);
    setElapsed(0);
    track("story_spotlight_change", { index: next, id: SPOTLIGHT[next].id });
  };

  const story = SPOTLIGHT[index];

  return (
    <figure
      className="cas-spotlight"
      data-testid="stories-spotlight"
      onMouseEnter={() => (hover.current = true)}
      onMouseLeave={() => (hover.current = false)}
    >
      <div className="cas-spot-progress" aria-hidden="true">
        <i style={{ width: `${(elapsed / ROTATE_MS) * 100}%` }} />
      </div>
      <div className="cas-spot-body" key={story.id} aria-live="polite">
        <span className="cas-spot-topic">{topicLabel(story.topic)}</span>
        <blockquote className="cas-spot-quote" data-testid="stories-spotlight-quote">{story.quote}</blockquote>
        <figcaption><Person story={story} /></figcaption>
      </div>
      <div className="cas-spot-controls">
        <div className="cas-dots" role="tablist" aria-label="Featured stories">
          {SPOTLIGHT.map((s, i) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={`Story ${i + 1}: ${s.headline}`}
              data-testid={`stories-spotlight-tab-${i}`}
              onClick={() => go(i)}
            />
          ))}
        </div>
        <span className="cas-spot-counter" data-testid="stories-spotlight-counter">
          {String(index + 1).padStart(2, "0")} / {String(SPOTLIGHT.length).padStart(2, "0")}
        </span>
        <div className="cas-spot-buttons">
          <button type="button" className="cas-icon-btn" aria-label="Previous story" data-testid="stories-spotlight-prev" onClick={() => go(index - 1)}><ChevronLeft size={16} /></button>
          <button
            type="button"
            className="cas-icon-btn"
            aria-label={paused ? "Resume auto-play" : "Pause auto-play"}
            aria-pressed={paused}
            data-testid="stories-spotlight-toggle"
            onClick={() => setPaused((p) => !p)}
          >
            {paused ? <Play size={14} /> : <Pause size={14} />}
          </button>
          <button type="button" className="cas-icon-btn" aria-label="Next story" data-testid="stories-spotlight-next" onClick={() => go(index + 1)}><ChevronRight size={16} /></button>
        </div>
      </div>
    </figure>
  );
};

const StoryCard = ({ story, index, open, onToggle }) => (
  <article
    className={`cas-card${open ? " is-open" : ""}`}
    style={{ "--i": index }}
    data-testid={`story-card-${story.id}`}
  >
    <span className="cas-card-topic">{topicLabel(story.topic)}</span>
    <h3>{story.headline}</h3>
    <blockquote>{story.quote}</blockquote>
    {open && (
      <div className="cas-card-more" id={`story-full-${story.id}`} data-testid={`story-full-${story.id}`}>
        <p>{story.story}</p>
        <div className="cas-helped" aria-label="What helped most">
          {story.helped.map((h) => <span key={h}>{h}</span>)}
        </div>
      </div>
    )}
    <footer className="cas-card-foot">
      <Person story={story} compact />
      <button
        type="button"
        className="cas-more"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`story-full-${story.id}`}
        data-testid={`story-toggle-${story.id}`}
      >
        {open ? <Minus size={14} /> : <Plus size={14} />} {open ? "Less" : "Read story"}
      </button>
    </footer>
  </article>
);

const whatsappHref = `https://wa.me/${eventConfig.brand.whatsappNumber}?text=${encodeURIComponent(
  "Hi, I'd like to view real testimonials from your past CPF seminars."
)}`;

const AttendeeStories = ({ onRegister }) => {
  const [topic, setTopic] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const reduceMotion = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const filtered = useMemo(
    () => (topic === "all" ? ATTENDEE_STORIES : ATTENDEE_STORIES.filter((s) => s.topic === topic)),
    [topic]
  );
  const visible = showAll ? filtered : filtered.slice(0, INITIAL_VISIBLE);
  const hiddenCount = filtered.length - visible.length;

  const selectTopic = (key) => {
    setTopic(key);
    setOpenId(null);
    setShowAll(false);
    track("story_filter", { topic: key });
  };

  const toggle = (id) => {
    const next = openId === id ? null : id;
    setOpenId(next);
    if (next) track("story_expand", { id });
  };

  const handleReserve = () => {
    analytics.primaryCtaClick({ source: "attendee-stories" });
    if (typeof onRegister === "function") onRegister();
  };

  return (
    <section className="cas-section" id="past-events" aria-labelledby="cas-title" data-testid="past-event-stories">
      <div className="cas-container">
        <header className="cas-heading">
          <p className="cas-eyebrow">Attendee stories</p>
          <h2 id="cas-title">What past attendees took home.</h2>
          <p className="cas-intro">
            In their own words, from our CPF seminars in May 2025, March 2026 and August 2026. All reviews are anonymous to protect attendees&rsquo; identities.
          </p>
        </header>

        <Spotlight reduceMotion={reduceMotion} />

        <div className="cas-filters" role="group" aria-label="Filter stories by topic" data-testid="stories-filters">
          {STORY_TOPICS.map((t) => (
            <button
              key={t.key}
              type="button"
              className="cas-chip"
              aria-pressed={topic === t.key}
              data-testid={`stories-filter-${t.key}`}
              onClick={() => selectTopic(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <p className="cas-filter-status" aria-live="polite" data-testid="stories-filter-status">
          {filtered.length} {filtered.length === 1 ? "story" : "stories"}{topic !== "all" ? ` about ${topicLabel(topic)}` : ""}
        </p>

        <div className="cas-wall" data-testid="stories-wall" key={topic}>
          {visible.length === 0 ? (
            <div className="cas-empty">No stories for this topic yet.</div>
          ) : (
            visible.map((s, i) => (
              <StoryCard key={s.id} story={s} index={i} open={openId === s.id} onToggle={() => toggle(s.id)} />
            ))
          )}
        </div>

        {hiddenCount > 0 && (
          <div className="cas-showmore">
            <button type="button" className="cas-ghost" onClick={() => setShowAll(true)} data-testid="stories-show-more">
              Show {hiddenCount} more {hiddenCount === 1 ? "story" : "stories"}
            </button>
          </div>
        )}

        <div className="cas-foot">
          <p data-testid="testimonials-privacy-note">
            All reviews on this landing page are anonymous to protect the identity of our attendees. Stories reflect individual experiences at past seminars and are not financial advice.
            You are welcome to{" "}
            <a href={whatsappHref} target="_blank" rel="noopener noreferrer" data-testid="testimonials-contact-whatsapp"><MessageCircle size={12} /> WhatsApp</a>
            {" "}or{" "}
            <a href={`mailto:${eventConfig.brand.contactEmail}?subject=${encodeURIComponent("Request to view seminar testimonials")}`} data-testid="testimonials-contact-email"><Mail size={12} /> email us</a>
            {" "}to view real testimonials.
          </p>
          <button type="button" className="cas-cta" onClick={handleReserve} data-testid="past-events-reserve-button">
            Reserve My Complimentary Seat <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default AttendeeStories;
